import { ref } from 'vue'
import { useUserStore } from '@/lib/stores'
import { FeatureConfig } from '@/lib/services'
import {FREE_TRIAL_ALERT, FEATURE_LIMIT_REACHED_ALERT} from '@/lib/utils/constants'

export interface AlertModalData {
  show: boolean
  key: string
  count: number
  message: string
  title: string
  subtitle: string
  description: string
}

export type FeatureWithPackageMeta = FeatureConfig & {
  product_name?: string
  start_date?: string
  renewal_date?: string
  subscription_type?: string
}

export const AlertModalService = () => {
  const packageFeatures = ref<FeatureWithPackageMeta[]>([])
  const state = ref<AlertModalData>({
    show: false,
    key: '',
    count: 0,
    message: '',
    title: '',
    subtitle: '',
    description: '',
  })

  // Load and cache package features once per session.
  function loadPackageFeatures(): void {
    if (packageFeatures.value.length > 0) {
      return
    }

    const userStore = useUserStore()
    const packages = userStore.getTrailPackageFeatures
    Object.values(packages).forEach((pack: any) => {
      pack.features?.forEach((feature: FeatureConfig) => {
        packageFeatures.value.push({
          ...feature,
          package_name: pack.package_name,
          product_name: pack.product_name,
          start_date: pack.start_date,
          renewal_date: pack.renewal_date,
          subscription_type: pack.subscription_type
        })
      })
    })
  }

  // Exposed helper to fetch a feature config by key.
  function getFeatureByKey(key: string): FeatureWithPackageMeta | undefined {
    if (!key) {
      return undefined
    }

    loadPackageFeatures()
    return packageFeatures.value.find((feature: FeatureWithPackageMeta) => feature.feature_key === key)
  }

  // Returns the current modal payload.
  function getState(): AlertModalData {
    return state.value
  }

  function showAlert(
    key: string = '', 
    count: number = 0, 
    message?: string, 
    hidePopup: boolean = false,
    title?: string,
    subtitle?: string,
    description?: string
  ): boolean {
    if (state.value.show && !key) {
      return true
    }

    const userStore = useUserStore()
    const isfreeTrailAccount = userStore.getIsTrailAccount;

    const alertTitle = isfreeTrailAccount ? FREE_TRIAL_ALERT.title : FEATURE_LIMIT_REACHED_ALERT.title;
    const alertSubtitle = isfreeTrailAccount ? FREE_TRIAL_ALERT.subtitle : FEATURE_LIMIT_REACHED_ALERT.subtitle;
    const alertDescription = isfreeTrailAccount ? FREE_TRIAL_ALERT.description : FEATURE_LIMIT_REACHED_ALERT.description;

    // If no key provided, skip validation and just show the modal
    if (!key) {
      state.value.key = ''
      state.value.count = 0
      state.value.message = message || ''
      state.value.title = title || alertTitle
      state.value.subtitle = subtitle || alertSubtitle
      state.value.description = description || message || alertDescription
      state.value.show = !hidePopup
      return true
    }

    // Validate usage against the package limit when key is provided.
    loadPackageFeatures()
    try {
      const feature = getFeatureByKey(key)
      
      if(!feature || feature.value_type !== 'limit') {
        return false
      }
      
      if(count < Number(feature.value) || Number(feature.value) === -1) {
        return false // limit not reached
      }
      
      // Limit reached - show modal
      state.value.key = key
      state.value.count = count
      state.value.message = message || ''
      state.value.title = title || alertTitle
      state.value.subtitle = subtitle || alertSubtitle
      state.value.description = description || message || alertDescription
      state.value.show = !hidePopup
      
      return true
    } catch (error) {
      console.error('Error showing modal:', error)
      return false
    }
  }

  function hide(): void {
    state.value.show = false
    state.value.key = ''
    state.value.count = 0
    state.value.message = ''
    state.value.title = ''
    state.value.subtitle = ''
    state.value.description = ''
  }

  function isFeatureAvailable(key: string): boolean {
    const userStore = useUserStore()
    const features = userStore.getPackageFeatures;
    return features.some((feature: FeatureWithPackageMeta) => feature.feature_key === key)
  }

  return {
    hide,
    showAlert,
    getState,
    getFeatureByKey,
    isFeatureAvailable,
    state
  };
}

export const alertModalService = AlertModalService();
